import type { ObservableMap } from 'mobx'
import { action, computed, makeObservable } from 'mobx'
import { Cubit } from '../../core'
import { CollaborationDataType, type MeetingCubit } from '../../MeetingCubit'
import type { Slide } from '../slides/Slide'
import { SlideQuestionType } from '../../../models/SlideQuestionType'
import type { MeetingQuestion } from '../MeetingQuestion'
import { RoomStateCollaborationData } from '../../../models/RoomStateCollaborationData'
import { upsertRoomStateCollaborationData } from '../../../firestore/RoomStateCollaborationData'
import {
  deleteRoomStateFile,
  getRoomStateFiles,
  supportedExtensions,
  uploadRoomStateFile,
} from '../../../firestore/RoomStateFile'
import type { StaticModelCollection } from '../../../types'
import { RoomStateFile } from '../../../models/RoomStateFile'
import { SimpleObservableValue } from '../../../stores/SimpleObservableValue'
import { EventEmitterTyped } from '../../../util/EventEmitterTyped'
import type { NextSlideButtonBlockReason } from '../../../types/meeting'

export type Options = unknown

export class InteractiveFormController extends Cubit {
  meeting: MeetingCubit
  slide: Slide
  options: Options

  files: StaticModelCollection<RoomStateFile>

  yoloMode = new SimpleObservableValue<boolean>(false)

  private lastMoveWasForward = false

  events = new EventEmitterTyped<{
    segmentOutlineRequest: [agentId: string, questionId: string]
    segmentCleanupResponse: [agentId: string, questionId: string]
  }>()

  constructor(meeting: MeetingCubit, slide: Slide, options: Options = {}) {
    super()
    this.meeting = meeting
    this.slide = slide
    this.options = options

    makeObservable(this)

    this.files = RoomStateFile.emptyCollection(meeting.repository)
  }

  initialize(): void {
    const initialIndex = this.elements.findIndex((q) => !q.groupAnswer)
    this.updateCurrentElementIndex(initialIndex === -1 ? 0 : initialIndex)

    this.addStream(
      getRoomStateFiles(this.meeting.repository, {
        roomStateId: this.meeting.roomId,
      }),
      (files) => {
        this.files.replaceModels(files)
      }
    )
    this.meeting.addShouldBlockNextSlideButtonCallback(
      this.slide.id,
      this.shouldBlockNextSlideButton
    )
  }

  shouldBlockNextSlideButton = (): NextSlideButtonBlockReason | false => {
    if (this.isNotLastPage) return 'interactive_form_not_complete'

    // now, if on last page, we must have the answer for last question
    const lastQuestion = this.elements[this.elements.length - 1]
    if (!lastQuestion) return false
    if (!lastQuestion.groupAnswer) return 'interactive_form_not_complete'

    return false
  }

  @computed
  get interactiveFormCollaborationData() {
    return (
      this.meeting.collaborationData.models.find(
        (d) => d.id === `interactive-form-${this.slide.id}`
      ) ?? RoomStateCollaborationData.empty(this.meeting.repository)
    )
  }

  @computed
  get currentElementIndex(): number {
    return (
      this.interactiveFormCollaborationData.data.data?.currentElementIndex ?? 0
    )
  }

  @action
  updateCurrentElementIndex(index: number) {
    if (!this.isUserListenedToByBot) return

    upsertRoomStateCollaborationData(this.meeting.repository, {
      roomId: this.meeting.roomId,
      id: `interactive-form-${this.slide.id}`,
      data: {
        data: {
          currentElementIndex: index,
        },
        collaborationDataType: CollaborationDataType.interactiveForm,
        userId: this.meeting.currentUser.uid,
        questionId: this.slide.id,
      },
    })
  }

  /**
   * Whether the current user is the one the bot is currently listening to
   */
  @computed
  get isUserListenedToByBot() {
    // in the context of an interactive form, the leader is the first user in the meeting
    // this is the one the bot listens to
    if (this.meeting.userIds?.length === 0) return false
    return this.meeting.userIds?.[0] === this.meeting.currentUser.uid
  }

  @computed
  get currentElement(): MeetingQuestion | null {
    return this.elements[this.currentElementIndex] ?? null
  }

  @action
  goToNextPage() {
    if (this.currentElementIndex >= this.elements.length) return
    // Mark direction of travel as forward
    this.lastMoveWasForward = true
    this.updateCurrentElementIndex(
      Math.min(this.elements.length, this.currentElementIndex + 1)
    )
  }

  @action
  goToPreviousPage() {
    if (this.currentElementIndex <= 0) return
    // Mark direction of travel as backward
    this.lastMoveWasForward = false
    this.updateCurrentElementIndex(Math.max(0, this.currentElementIndex - 1))
  }

  @computed
  get elements() {
    const questions = this.slide.slideQuestions ?? []

    return questions
      .filter(
        (q) => q.questionType === SlideQuestionType.interactiveFormElement
      )
      .sort((a, b) => (a.data.order ?? 0) - (b.data.order ?? 0))
  }

  formUpdateTimeout: NodeJS.Timeout | null = null
  sendFormUpdate(values: ObservableMap<string, unknown>) {
    const message = `User has updated the form. The current values are: ${JSON.stringify(
      Object.fromEntries(values)
    )}`

    // debounce rapid updates
    if (this.formUpdateTimeout) clearTimeout(this.formUpdateTimeout)
    this.formUpdateTimeout = setTimeout(() => {
      this.meeting.sendMessageToSoloBotRequest(message, {
        requestSoloBotResponse: true,
        requestSoloBotInterrupt: true,
        sendToSoloBot: true,
      })
    }, 600)
  }

  suggestionAcceptedTimeout: NodeJS.Timeout | null = null
  suggestionAccepted(values: ObservableMap<string, unknown>) {
    const message = `User has accepted the suggestion. The current values are: ${JSON.stringify(
      Object.fromEntries(values)
    )}`

    // debounce rapid updates
    if (this.suggestionAcceptedTimeout)
      clearTimeout(this.suggestionAcceptedTimeout)
    this.suggestionAcceptedTimeout = setTimeout(() => {
      this.meeting.sendMessageToSoloBotRequest(message, {
        requestSoloBotResponse: false,
        sendToSoloBot: true,
      })
    }, 600)
  }

  suggestionRejectedTimeout: NodeJS.Timeout | null = null
  suggestionRejected(values: ObservableMap<string, unknown>) {
    const message = `User has rejected suggestion. The current values are: ${JSON.stringify(
      Object.fromEntries(values)
    )}`

    // debounce rapid updates
    if (this.suggestionRejectedTimeout)
      clearTimeout(this.suggestionRejectedTimeout)
    this.suggestionRejectedTimeout = setTimeout(() => {
      this.meeting.sendMessageToSoloBotRequest(message, {
        requestSoloBotResponse: true,
        sendToSoloBot: true,
      })
    }, 600)
  }

  sendFormMountMessage = () => {
    if (this.elements.length === 0) return

    const answers = this.elements.map(
      (q) => q.groupAnswer?.data.answerString ?? '{}'
    )

    if (answers.length === 0) return

    this.meeting.sendMessageToSoloBotRequest(
      `User has opened the interactive form. Here are the current answers:  ${answers.join('\n')}`,
      {
        requestSoloBotResponse: false,
        sendToSoloBot: true,
      }
    )
  }

  sendFormElementStartedRequest = (
    questionId: string,
    fieldsNeedingAutocomplete: string[] = [],
    fieldsToNeverAutocomplete: string[] = []
  ) => {
    const messageParts = [`User is on form element ${questionId}.`]

    if (fieldsNeedingAutocomplete.length > 0) {
      const joined = fieldsNeedingAutocomplete.join(', ')
      messageParts.push(
        `The user would like autocomplete suggestions for the following fields: ${joined}.`
      )
      messageParts.push(
        'Generate suggestions for ALL the fields that have been requested.'
      )
      messageParts.push(
        'Generate the suggestions and send them, then ask the user if they would like to tweak them.'
      )
    }

    if (fieldsToNeverAutocomplete.length > 0) {
      const joined = fieldsToNeverAutocomplete.join(', ')
      messageParts.push(
        `The user does NOT want autocomplete suggestions for the following fields: ${joined}.`
      )
      messageParts.push('Do NOT generate suggestions for any of these fields.')
    }

    messageParts.push(
      `The state of the form is as follows:\n ${this.getAnswersSoFar()}`
    )

    const message = messageParts.join('\n\n')

    const hasAnswers = this.hasAnswersForQuestion(questionId)
    const shouldRequestResponse = this.lastMoveWasForward && !hasAnswers

    this.meeting.sendMessageToSoloBotRequest(message, {
      requestSoloBotInterrupt: true,
      requestSoloBotResponse: shouldRequestResponse,
      sendToSoloBot: true,
    })
  }

  sendPreviewJSONRequest(agentId: string, questionId: string) {
    if (!agentId) return ''
    if (this === undefined) return ''

    const message = `Build the final JSON that represents this experience.\n ${this.getAnswersSoFar()}`

    const requestId = this.meeting.sendMessageToSoloBotRequest(message, {
      requestSoloBotInterrupt: true,
      requestSoloBotResponse: false,
      agentId: agentId,
      formElementId: questionId,
      sendToSoloBot: false,
    })

    return requestId
  }

  getAnswersSoFar() {
    let message = ''
    const answers = this.elements.map(
      (q) => q.groupAnswer?.data.answerString ?? '{}'
    )

    for (const answerString of answers) {
      try {
        const answer = JSON.parse(answerString)
        for (const [key, value] of Object.entries(answer)) {
          message += `${key}: ${JSON.stringify(value)}\n`
        }
      } catch {
        // ignore JSON parse errors
      }
    }

    return message
  }

  getInteractiveFormValuesWithName(name: string) {
    const values: string[] = []
    const answers = this.elements.map(
      (q) => q.groupAnswer?.data.answerString ?? '{}'
    )

    for (const answerString of answers) {
      try {
        const answer = JSON.parse(answerString)
        if (!answer[name]) continue
        if (typeof answer[name] !== 'string') continue
        values.push(answer[name])
      } catch {
        // ignore JSON parse errors
      }
    }

    return values
  }

  sendRegenerateExhibitRequest(
    agentId: string,
    questionId: string,
    segmentId: string,
    slideJSON: string,
    segmentsJSON: string,
    notes: string = ''
  ) {
    const prompt = `Please regenerate the following exhibit in segment ${segmentId}.

    This is the full JSON of the experience, including the segment that the exhibit belongs to, in case you need context:
    ${segmentsJSON}

    This is the current value we are requesting to be updated:
    ${slideJSON}

    ${notes ? `Here are some additional notes that the user has provided: ${notes}` : ''}
  `

    const requestId = this.meeting.sendMessageToSoloBotRequest(prompt.trim(), {
      requestSoloBotResponse: false,
      agentId: agentId,
      formElementId: questionId,
      sendToSoloBot: false,
    })

    return requestId
  }

  sendRegenerateSlideRequest(
    agentId: string,
    questionId: string,
    segmentId: string,
    slideJSON: string,
    segmentsJSON: string,
    notes: string = ''
  ) {
    const prompt = `Please regenerate the following slide in segment ${segmentId}.

    This is the full JSON of the experience, including the segment that the slide belongs to, in case you need context:
    ${segmentsJSON}

    This is the current value we are requesting to be updated:
    ${slideJSON}

    ${notes ? `Here are some additional notes that the user has provided: ${notes}` : ''}
  `

    const requestId = this.meeting.sendMessageToSoloBotRequest(prompt.trim(), {
      requestSoloBotResponse: false,
      agentId: agentId,
      formElementId: questionId,
      sendToSoloBot: false,
    })

    return requestId
  }

  sendRegenerateSegmentRequest(
    agentId: string,
    questionId: string,
    segmentId: string,
    segmentsJSON: string,
    notes: string = ''
  ) {
    const prompt = `Please regenerate the following segment: ${segmentId}.

    This is the full JSON of the experience, including the segment that the slide belongs to, in case you need context:
    ${segmentsJSON}

    ${notes ? `Here are some additional notes that the user has provided: ${notes}` : ''}

    Please return only the JSON for a single segment, do not return the entire segments JSON.
  `

    const requestId = this.meeting.sendMessageToSoloBotRequest(prompt.trim(), {
      requestSoloBotResponse: false,
      agentId: agentId,
      formElementId: questionId,
      sendToSoloBot: false,
    })

    return requestId
  }

  sendSegmentOutlineRequest(agentId: string, questionId: string) {
    if (!agentId) return ''

    const message = `We are building an experience for learning.\n ${this.getAnswersSoFar()}`

    const requestId = this.meeting.sendMessageToSoloBotRequest(message, {
      requestSoloBotInterrupt: true,
      requestSoloBotResponse: false,
      agentId: agentId,
      formElementId: questionId,
      sendToSoloBot: false,
    })

    this.events.emit('segmentOutlineRequest', agentId, questionId)

    return requestId
  }

  sendSegmentCleanupRequest(
    agentId: string,
    questionId: string,
    message: string
  ) {
    const requestId = this.meeting.sendMessageToSoloBotRequest(message, {
      sendToSoloBot: false,
      requestSoloBotResponse: false,
      agentId: agentId,
      formElementId: questionId,
    })

    this.events.emit('segmentCleanupResponse', agentId, questionId)

    return requestId
  }

  get supportedExtensions() {
    return supportedExtensions
  }

  uploadFile = async (file: File) => {
    if (!this.meeting.currentUserIsGroupLeader) return

    const questionId = this.currentElement?.id
    if (!questionId) return

    return uploadRoomStateFile(this.meeting.repository, {
      roomStateId: this.meeting.roomId,
      file,
    })
  }

  deleteFile = async (fileId: string) => {
    if (!this.meeting.currentUserIsGroupLeader) return

    const file = this.files.models.find((f) => f.id === fileId)
    if (!file) return

    return deleteRoomStateFile(this.meeting.repository, {
      roomStateId: this.meeting.roomId,
      fileId: file.id,
    })
  }

  @computed
  get isNotLastPage() {
    return this.currentElementIndex < this.elements.length - 1
  }

  @computed
  get isNotFirstPage() {
    return this.currentElementIndex > 0
  }

  private hasAnswersForQuestion(questionId: string): boolean {
    return this.meeting.groupAnswers.some(
      (a) => a.data.slideQuestionId === questionId
    )
  }
}
